/**
 * The effect interpreter. Every op, target mode, filter field, condition and
 * amount kind in cardTypes.ts is implemented here and nowhere else.
 *
 * Nothing in this file branches on a card name or id.
 */

import type { Amount, Chain, Effect, Filter, Target, Who } from './cardTypes';
import { profileOf } from './cardDb';
import {
  addBuff,
  addGas,
  blacklistObject,
  createTokenObject,
  // bounce is implemented locally so tokens cease after the hand move

  damagePlayer,
  dealDamage,
  destroyObject,
  discardCard,
  drawCards,
  emit,
  enterBattlefield,
  exhaustObject,
  fireTriggers,
  freezeObject,
  grantKeywordTo,
  millCards,
  moveObject,
  readyObject,
  removeKeywordFrom,
  sacrificeObject,
  shuffleDeck,
  type Ctx,
} from './mutate';
import {
  allBattlefield,
  charactersOf,
  evalAmount,
  evalCondition,
  getEffectiveStats,
  isPlayerId,
  matchesFilter,
  nameOf,
  opponentOf,
  profile,
} from './query';
import type { GameState, StackItem, TargetSlotSpec } from './types';

// ---------------------------------------------------------------------------
// Target slots
// ---------------------------------------------------------------------------

export interface SlotRef {
  path: number[];
  target: Target;
  /** The shared slot name, when the card gave one. */
  slot?: string;
}

/** One choose-mode target as it appears in the effect tree, in resolution order. */
interface ChooseOccurrence {
  path: number[];
  target: Target;
  /** Index into SlotPlan.slots, which is what item.targets is aligned to. */
  slotIndex: number;
}

/**
 * The choice layout of an effect list: the prompts a player answers, and the
 * order the interpreter consumes them in.
 */
export interface SlotPlan {
  /** One entry per prompt. Index-aligned with StackItem.targets. */
  slots: SlotRef[];
  /** Every choose target in pre-order, each pointing at its prompt. */
  occurrences: ChooseOccurrence[];
}

/** Does this effect carry a target field? */
function targetOf(effect: Effect): Target | undefined {
  return 'target' in effect ? (effect as { target: Target }).target : undefined;
}

/**
 * Walk an effect list in a fixed pre-order (array order, then `then` branch,
 * then `else` branch) and work out its choices.
 *
 * Choose targets that share a non-empty `slot` name within one ability are ONE
 * prompt: the player picks once and every effect with that slot uses the same
 * selection. The first occurrence's spec wins, which the validator enforces by
 * rejecting a slot name whose occurrences disagree. A target with no `slot`
 * gets a prompt of its own, which is the original behaviour.
 *
 * Play time and resolution time both use this function, which is what keeps
 * `item.targets` index-aligned.
 */
export function planChoices(effects: Effect[]): SlotPlan {
  const slots: SlotRef[] = [];
  const occurrences: ChooseOccurrence[] = [];
  const byName = new Map<string, number>();

  const walk = (list: Effect[], path: number[]): void => {
    list.forEach((effect, i) => {
      const here = [...path, i];
      if (effect.op === 'conditional') {
        walk(effect.then, [...here, 0]);
        if (effect.else) walk(effect.else, [...here, 1]);
        return;
      }
      if (effect.op === 'sequence') {
        walk(effect.effects, here);
        return;
      }
      const t = targetOf(effect);
      if (!t || t.mode !== 'choose') return;
      const name = t.slot && t.slot.length > 0 ? t.slot : undefined;
      if (name !== undefined) {
        const existing = byName.get(name);
        if (existing !== undefined) {
          occurrences.push({ path: here, target: t, slotIndex: existing });
          return;
        }
        byName.set(name, slots.length);
      }
      occurrences.push({ path: here, target: t, slotIndex: slots.length });
      slots.push({ path: here, target: t, slot: name });
    });
  };

  walk(effects, []);
  return { slots, occurrences };
}

/**
 * The prompts an effect list asks for, one per distinct choice.
 * Index-aligned with StackItem.targets.
 */
export function collectSlots(effects: Effect[]): SlotRef[] {
  return planChoices(effects).slots;
}

/** How many choose targets a branch contains, for skipping an untaken one. */
function countOccurrences(effects: Effect[]): number {
  return planChoices(effects).occurrences.length;
}

// ---------------------------------------------------------------------------
// Candidate enumeration
// ---------------------------------------------------------------------------

function candidateIds(state: GameState, type: Target['type']): string[] {
  switch (type) {
    case 'character':
      return state.playerOrder.flatMap((p) => state.players[p].battlefield.characters.slice());
    case 'wallet':
      return state.playerOrder.flatMap((p) => state.players[p].battlefield.wallets.slice());
    case 'protocol':
      return state.playerOrder.flatMap((p) => state.players[p].battlefield.protocols.slice());
    case 'upgrade':
      return state.playerOrder.flatMap((p) => state.players[p].battlefield.upgrades.slice());
    case 'permanent':
      return state.playerOrder.flatMap((p) => [
        ...state.players[p].battlefield.characters,
        ...state.players[p].battlefield.protocols,
        ...state.players[p].battlefield.upgrades,
      ]);
    case 'player':
      return state.playerOrder.slice();
    case 'any':
      return [
        ...state.playerOrder.flatMap((p) => state.players[p].battlefield.characters.slice()),
        ...state.playerOrder.slice(),
      ];
    case 'stackItem':
      return state.stack.map((s) => s.id);
    case 'cardInDust':
      return state.playerOrder.flatMap((p) => state.players[p].dust.slice());
    case 'cardInHand':
      return state.playerOrder.flatMap((p) => state.players[p].hand.slice());
    default:
      return [];
  }
}

function whoMatchesPlayer(who: Who | undefined, pid: string, controller: string): boolean {
  switch (who ?? 'any') {
    case 'friendly':
      return pid === controller;
    case 'enemy':
      return pid !== controller;
    default:
      return true;
  }
}

/**
 * Every id this target could legally select right now. Cold Storage removes
 * the opponent's permanents from `choose` targeting only (RULES 7).
 */
export function legalTargets(
  state: GameState,
  target: Target,
  controller: string,
  sourceInstanceId: string | undefined,
  excludeStackItemId?: string,
): string[] {
  const out: string[] = [];
  for (const id of candidateIds(state, target.type)) {
    if (target.type === 'player') {
      if (whoMatchesPlayer(target.who, id, controller)) out.push(id);
      continue;
    }
    if (target.type === 'stackItem') {
      if (id === excludeStackItemId) continue;
      const item = state.stack.find((s) => s.id === id);
      if (!item) continue;
      if (!whoMatchesPlayer(target.who, item.controller, controller)) continue;
      // Only cards on the stack can be reverted, matching the printed rules.
      if (item.kind !== 'card') continue;
      if (target.filter) {
        const o = state.objects[item.sourceInstanceId];
        if (!o || !matchesFilter(state, o, target.filter, 'any', controller, sourceInstanceId)) continue;
      }
      out.push(id);
      continue;
    }
    if (target.type === 'any' && isPlayerId(state, id)) {
      if (whoMatchesPlayer(target.who, id, controller)) out.push(id);
      continue;
    }
    const o = state.objects[id];
    if (!o) continue;
    if (!matchesFilter(state, o, target.filter, target.who, controller, sourceInstanceId)) continue;
    if (target.mode === 'choose' && o.controller !== controller) {
      if (getEffectiveStats(state, id).keywords.includes('coldStorage')) continue;
    }
    out.push(id);
  }
  return out;
}

/** The slot specs a UI or bot needs to pick targets for an item. */
export function slotSpecs(
  state: GameState,
  effects: Effect[],
  controller: string,
  sourceInstanceId: string | undefined,
  excludeStackItemId?: string,
): TargetSlotSpec[] {
  return collectSlots(effects).map((slot) => ({
    path: slot.path,
    slot: slot.slot,
    type: slot.target.type,
    count: slot.target.count ?? 1,
    optional: slot.target.optional === true,
    legal: legalTargets(state, slot.target, controller, sourceInstanceId, excludeStackItemId),
  }));
}

/**
 * Validate a player's target selection against the slot specs. Throws nothing;
 * returns a reason string when invalid.
 */
export function validateSelection(
  specs: TargetSlotSpec[],
  selection: string[][],
): { ok: true } | { ok: false; code: 'NO_LEGAL_TARGET' | 'ILLEGAL_TARGET'; reason: string } {
  for (let i = 0; i < specs.length; i++) {
    const spec = specs[i];
    const chosen = selection[i] ?? [];
    if (chosen.length === 0) {
      if (spec.optional) continue;
      if (spec.legal.length === 0) {
        return { ok: false, code: 'NO_LEGAL_TARGET', reason: 'There is no legal target for that.' };
      }
      return { ok: false, code: 'ILLEGAL_TARGET', reason: 'You must choose a target.' };
    }
    if (chosen.length > spec.count) {
      return { ok: false, code: 'ILLEGAL_TARGET', reason: 'Too many targets chosen.' };
    }
    if (new Set(chosen).size !== chosen.length) {
      return { ok: false, code: 'ILLEGAL_TARGET', reason: 'The same target was chosen twice.' };
    }
    for (const id of chosen) {
      if (!spec.legal.includes(id)) {
        return { ok: false, code: 'ILLEGAL_TARGET', reason: 'That is not a legal target.' };
      }
    }
  }
  return { ok: true };
}

/**
 * True when the item had choose-mode targets and none of them are legal any
 * more, which makes the item fizzle (RULES 4).
 */
export function allTargetsIllegal(state: GameState, item: StackItem): boolean {
  const slots = collectSlots(item.effects);
  if (slots.length === 0) return false;
  let hadAny = false;
  for (let i = 0; i < slots.length; i++) {
    const chosen = item.targets[i] ?? [];
    if (chosen.length === 0) continue;
    hadAny = true;
    const stillLegal = legalTargets(
      state,
      slots[i].target,
      item.controller,
      item.sourceInstanceId,
      item.id,
    );
    if (chosen.some((id) => stillLegal.includes(id))) return false;
  }
  return hadAny;
}

// ---------------------------------------------------------------------------
// Execution
// ---------------------------------------------------------------------------

interface ExecCtx {
  ctx: Ctx;
  item: StackItem;
  /** The item's choice layout, computed once. */
  plan: SlotPlan;
  /** Cursor into plan.occurrences, advanced in pre-order as effects run. */
  occurrence: number;
}

/** Run every effect of a stack item. */
export function executeEffects(ctx: Ctx, item: StackItem): void {
  const exec: ExecCtx = { ctx, item, plan: planChoices(item.effects), occurrence: 0 };
  runList(exec, item.effects);
}

function runList(exec: ExecCtx, effects: Effect[]): void {
  for (const effect of effects) runEffect(exec, effect);
}

function resolveTarget(exec: ExecCtx, target: Target): string[] {
  const { ctx, item } = exec;
  const state = ctx.state;
  const controller = item.controller;
  switch (target.mode) {
    case 'choose': {
      const occ = exec.plan.occurrences[exec.occurrence];
      exec.occurrence++;
      const chosen = occ ? item.targets[occ.slotIndex] ?? [] : [];
      const legal = legalTargets(state, target, controller, item.sourceInstanceId, item.id);
      return chosen.filter((id) => legal.includes(id));
    }
    case 'all':
      return legalTargets(state, target, controller, item.sourceInstanceId, item.id);
    case 'random': {
      const pool = legalTargets(state, target, controller, item.sourceInstanceId, item.id);
      return ctx.rng.pick(pool, target.count ?? 1);
    }
    case 'self':
      return item.sourceInstanceId ? [item.sourceInstanceId] : [];
    case 'host': {
      const src = state.objects[item.sourceInstanceId];
      return src?.attachedTo ? [src.attachedTo] : [];
    }
    case 'controller':
      return [controller];
    case 'opponent':
      return [opponentOf(state, controller)];
    case 'triggering':
      return item.triggeringInstanceId ? [item.triggeringInstanceId] : [];
    default:
      return [];
  }
}

function amountOf(exec: ExecCtx, amount: Amount): number {
  return evalAmount(
    exec.ctx.state,
    amount,
    exec.item.controller,
    exec.item.sourceInstanceId,
    exec.item.xValue,
  );
}

function whoPlayer(exec: ExecCtx, who: 'controller' | 'opponent'): string {
  return who === 'controller'
    ? exec.item.controller
    : opponentOf(exec.ctx.state, exec.item.controller);
}

function runEffect(exec: ExecCtx, effect: Effect): void {
  const { ctx, item } = exec;
  const state = ctx.state;
  const controller = item.controller;

  switch (effect.op) {
    case 'damage': {
      const n = amountOf(exec, effect.amount);
      for (const id of resolveTarget(exec, effect.target)) {
        if (isPlayerId(state, id)) damagePlayer(ctx, item.sourceInstanceId || null, id, n);
        else dealDamage(ctx, item.sourceInstanceId || null, id, n);
      }
      break;
    }
    case 'destroy': {
      for (const id of resolveTarget(exec, effect.target)) destroyObject(ctx, id, 'destroy effect');
      break;
    }
    case 'draw': {
      const n = amountOf(exec, effect.amount);
      if (effect.who === 'each') {
        for (const pid of state.playerOrder) drawCards(ctx, pid, n);
      } else {
        drawCards(ctx, whoPlayer(exec, effect.who), n);
      }
      break;
    }
    case 'discard': {
      const n = amountOf(exec, effect.amount);
      const pid = whoPlayer(exec, effect.who);
      const hand = state.players[pid].hand;
      if (n <= 0 || hand.length === 0) break;
      if (effect.random) {
        for (const id of ctx.rng.pick(hand, n)) discardCard(ctx, pid, id);
      } else if (hand.length <= n) {
        for (const id of hand.slice()) discardCard(ctx, pid, id);
      } else {
        state.pendingChoices.push({ kind: 'discard', player: pid, count: n });
      }
      break;
    }
    case 'gainIntegrity': {
      const n = amountOf(exec, effect.amount);
      changeIntegritySafe(exec, whoPlayer(exec, effect.who), n, 'gain');
      break;
    }
    case 'loseIntegrity': {
      const n = amountOf(exec, effect.amount);
      changeIntegritySafe(exec, whoPlayer(exec, effect.who), -n, 'lose');
      break;
    }
    case 'buff': {
      for (const id of resolveTarget(exec, effect.target)) {
        addBuff(ctx, id, effect.attack, effect.health, effect.duration);
      }
      break;
    }
    case 'grantKeyword': {
      for (const id of resolveTarget(exec, effect.target)) {
        grantKeywordTo(ctx, id, effect.keyword, effect.duration);
      }
      break;
    }
    case 'removeKeyword': {
      for (const id of resolveTarget(exec, effect.target)) {
        removeKeywordFrom(ctx, id, effect.keyword, effect.duration);
      }
      break;
    }
    case 'createToken': {
      const n = amountOf(exec, effect.amount);
      const pid = effect.who ? whoPlayer(exec, effect.who) : controller;
      let created = 0;
      for (let i = 0; i < n; i++) {
        if (createTokenObject(ctx, pid, effect.token)) created++;
      }
      if (created > 0) fireTriggers(ctx, 'token:create', { controller: pid });
      break;
    }
    case 'exhaust': {
      for (const id of resolveTarget(exec, effect.target)) exhaustObject(ctx, id);
      break;
    }
    case 'ready': {
      for (const id of resolveTarget(exec, effect.target)) readyObject(ctx, id);
      break;
    }
    case 'freeze': {
      for (const id of resolveTarget(exec, effect.target)) freezeObject(ctx, id);
      break;
    }
    case 'bounce': {
      for (const id of resolveTarget(exec, effect.target)) {
        const o = state.objects[id];
        if (!o) continue;
        // Bounce is a zone move, so HODL does not stop it (RULES 7).
        bounce(exec, id);
      }
      break;
    }
    case 'recover': {
      for (const id of resolveTarget(exec, effect.target)) {
        const o = state.objects[id];
        if (!o || o.zone !== 'dust') continue;
        const name = nameOf(state, id);
        if (effect.to === 'hand') {
          moveObject(ctx, id, 'hand');
        } else {
          const prof = profileOf(o);
          if (prof.type === 'upgrade') {
            // An Upgrade with no host cannot be recovered onto the battlefield.
            const host = charactersOf(state, o.controller)[0];
            if (!host) continue;
            enterBattlefield(ctx, id, { attachedTo: host.instanceId });
          } else {
            enterBattlefield(ctx, id, {});
          }
        }
        emit(ctx, {
          type: 'Recovered',
          turn: state.turn,
          player: o.controller,
          instanceId: id,
          name,
          to: effect.to,
        });
      }
      break;
    }
    case 'fetchWallet': {
      fetchWallet(exec, effect.chain ?? 'any', effect.to, effect.exhausted === true);
      break;
    }
    case 'addGas': {
      const n = amountOf(exec, effect.amount);
      addGas(ctx, controller, effect.chain as Chain | 'any', n);
      break;
    }
    case 'revert': {
      for (const id of resolveTarget(exec, effect.target)) {
        const idx = state.stack.findIndex((s) => s.id === id);
        if (idx < 0) continue;
        const [removed] = state.stack.splice(idx, 1);
        if (removed.kind === 'card') {
          const o = state.objects[removed.sourceInstanceId];
          if (o) moveObject(ctx, removed.sourceInstanceId, 'dust');
        }
        emit(ctx, { type: 'Reverted', turn: state.turn, player: controller, label: removed.label });
      }
      break;
    }
    case 'blacklist': {
      for (const id of resolveTarget(exec, effect.target)) blacklistObject(ctx, id);
      break;
    }
    case 'mill': {
      const n = amountOf(exec, effect.amount);
      millCards(ctx, whoPlayer(exec, effect.who), n);
      break;
    }
    case 'sacrifice': {
      const ids = resolveTarget(exec, effect.target);
      if (ids.length === 0 && effect.target.mode === 'choose') {
        // A trigger that reached resolution without a selection: ask now.
        const legal = legalTargets(state, effect.target, controller, item.sourceInstanceId, item.id);
        if (legal.length > 0) {
          state.pendingChoices.push({
            kind: 'sacrifice',
            player: controller,
            count: effect.target.count ?? 1,
            legal,
          });
        }
        break;
      }
      for (const id of ids) sacrificeObject(ctx, id);
      break;
    }
    case 'scry': {
      const n = amountOf(exec, effect.amount);
      const deck = state.players[controller].deck;
      const look = deck.slice(0, Math.max(0, n));
      if (look.length === 0) break;
      state.pendingChoices.push({ kind: 'scry', player: controller, cards: look });
      break;
    }
    case 'conditional': {
      const pass = evalCondition(state, effect.condition, controller, item.sourceInstanceId);
      if (pass) {
        runList(exec, effect.then);
        if (effect.else) exec.occurrence += countOccurrences(effect.else);
      } else {
        exec.occurrence += countOccurrences(effect.then);
        if (effect.else) runList(exec, effect.else);
      }
      break;
    }
    case 'sequence': {
      runList(exec, effect.effects);
      break;
    }
    default: {
      // Exhaustiveness guard: a new op must be implemented here.
      const never: never = effect;
      throw new Error(`Unimplemented effect op: ${JSON.stringify(never)}`);
    }
  }
}

function changeIntegritySafe(exec: ExecCtx, playerId: string, delta: number, kind: string): void {
  if (delta === 0) return;
  // loseIntegrity is not damage: it never triggers Vamp and is not prevented.
  const { ctx } = exec;
  const p = ctx.state.players[playerId];
  p.integrity += delta;
  emit(ctx, {
    type: 'IntegrityChanged',
    turn: ctx.state.turn,
    player: playerId,
    delta,
    total: p.integrity,
    reason: kind,
  });
  if (delta > 0) fireTriggers(ctx, 'integrity:gain', { controller: playerId });
}

function bounce(exec: ExecCtx, instanceId: string): void {
  const { ctx } = exec;
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return;
  const name = nameOf(state, instanceId);
  const controller = o.controller;
  o.controller = o.owner;
  moveObject(ctx, instanceId, 'hand');
  emit(ctx, { type: 'Bounced', turn: state.turn, player: controller, instanceId, name });
  if (o.tokenId) {
    moveObject(ctx, instanceId, 'limbo');
    emit(ctx, { type: 'TokenCeased', turn: state.turn, player: controller, instanceId, name });
  }
}

function fetchWallet(
  exec: ExecCtx,
  chain: Chain | 'any',
  to: 'hand' | 'battlefield',
  exhausted: boolean,
): void {
  const { ctx, item } = exec;
  const state = ctx.state;
  const controller = item.controller;
  const deck = state.players[controller].deck;
  const found = deck.find((id) => {
    const w = profile(state, id).wallet;
    if (!w || !w.basic) return false;
    return chain === 'any' || w.chain === chain || w.chain === 'any';
  });
  if (!found) {
    emit(ctx, {
      type: 'WalletFetched',
      turn: state.turn,
      player: controller,
      instanceId: null,
      name: 'nothing',
    });
    shuffleDeck(ctx, controller);
    return;
  }
  const name = nameOf(state, found);
  if (to === 'hand') moveObject(ctx, found, 'hand');
  else {
    const w = profile(state, found).wallet;
    enterBattlefield(ctx, found, { exhausted: exhausted || w?.entersExhausted === true });
  }
  emit(ctx, {
    type: 'WalletFetched',
    turn: state.turn,
    player: controller,
    instanceId: found,
    name,
  });
  shuffleDeck(ctx, controller);
}

// ---------------------------------------------------------------------------
// Helpers other modules need
// ---------------------------------------------------------------------------

/** Does any battlefield object match this filter for this controller? */
export function anyMatching(
  state: GameState,
  filter: Filter | undefined,
  who: Who,
  controller: string,
  sourceInstanceId?: string,
): boolean {
  return allBattlefield(state).some((o) =>
    matchesFilter(state, o, filter, who, controller, sourceInstanceId),
  );
}
